import { TemplateElement, html, defineElement } from '@webtides/element-js';
import { stringify } from 'qs';

const convertStringToHTML = (template) => {
    const parser = new DOMParser();
    const document = parser.parseFromString(template.toString(), 'text/html');
    return document.body;
};

/**
 * @property {string} contentPath
 * @property {number} pageSize
 * @property {number} currentPage
 * @property {array} pageIds
 * @property {string} storageKey
 */
export default class PagesWatchListComponent extends TemplateElement {
    constructor() {
        super({ shadowRender: true });
    }

    properties() {
        return {
            contentPath: '',
            pageSize: 7,
            currentPage: 0,
            pageIds: [],
            storageKey: 'watchedPages',
        };
    }

    connected() {
        if (this.pageIds.length > 0) return;
        this.pageIds = JSON.parse(window.localStorage.getItem(this.storageKey)) || [];
    }

    afterUpdate() {
        const nextPageContainer = this.querySelector('.next-page');
        const nextPageButton = this.querySelector('.next-page .btn');
        nextPageContainer.classList.toggle('d-none', !this.hasNextPage);

        nextPageButton.textContent =
          this.remainingPages > 1
            ? `${this.pageSize} ${nextPageButton.getAttribute('button-text-next')}`
            : nextPageButton.getAttribute('button-text-remaining');
    }

    watch() {
        return {
            pageIds: (pageIds) => {
                if (this.pageIds.length > 0) {
                    /** @type ShareButton */
                    const watchListShareButton = this.querySelector('[data-share-watch-list]');
                    if (watchListShareButton) {
                        const currentUrl = window.location.href.split('#')[0];
                        watchListShareButton.url = `${currentUrl}?pages=${this.pageIds.join(',')}${window.location.hash}`;
                    }

                    this.fetchPages();
                }
            },
        };
    }

    events() {
        return {
            document: {
                'watch-page': (e) => {
                    if (this.pageIds.length > 0) return;
                    this.fetchPages();
                },
            },
            this: {
                click: (e) => {
                    if (e.target.closest('[data-clear-watch-list]')) {
                        this.clearWatched();
                        return;
                    }

                    if (e.target.closest('[remove-from-list]')) {
                        const pageId = e.target.closest('[remove-from-list]').getAttribute('remove-from-list');
                        if (pageId) {
                            this.removeFromWatched(pageId);
                        }
                    }

                    if (e.target.closest('[load-next-page]')) {
                        e.preventDefault();
                        this.currentPage++;
                        this.appendPages();
                    }
                },
            },
        };
    }

    get hasNextPage() {
        const totalItems = this.pageIds.length;
        const totalPages = totalItems / this.pageSize;

        return this.currentPage < totalPages - 1;
    }

    get remainingPages() {
        const totalItems = this.pageIds.length;
        const totalPages = totalItems / this.pageSize;
        return totalPages - this.currentPage - 1;
    }

    get filteredPageIds() {
        const startItem = this.currentPage * this.pageSize;
        const endItem = startItem + this.pageSize;

        return this.pageIds.slice(startItem, endItem);
    }

    async fetchPage(page, showLoadingSpinner = true) {
        const filter = { pages: this.filteredPageIds, currentPage: page };
        const query = stringify(filter, { arrayFormat: 'comma' });

        if (showLoadingSpinner) this.dispatch('loading-start');
        const response = await fetch(`${this.contentPath}?${query}`);
        const htmlString = await response.text();
        if (showLoadingSpinner) this.dispatch('loading-end');
        return convertStringToHTML(htmlString);
    }

    fetchPages() {
        this.fetchPage(this.currentPage).then((html) => {
            const pages = html.querySelector('.pages');
            const container = this.querySelector('.pages');
            container.replaceChildren(...pages.childNodes);
        });
    }

    appendPages() {
        this.fetchPage(this.currentPage, false).then((html) => {
            const pages = html.querySelector('.pages');
            const container = this.querySelector('.pages');
            container.append(...pages.childNodes);
        });
    }

    clearWatched() {
        const pageIds = [];
        window.localStorage.setItem(this.storageKey, JSON.stringify(pageIds));
        this.pageIds = pageIds;
    }

    removeFromWatched(pageId) {
        const watchedPages = new Set(JSON.parse(window.localStorage.getItem(this.storageKey)));
        watchedPages.delete(pageId);
        const pageIds = Array.from(watchedPages);
        window.localStorage.setItem(this.storageKey, JSON.stringify(pageIds));
        this.pageIds = pageIds;
    }

    template() {
        if (this.pageIds.length > 0) {
            return html` <slot></slot> `;
        }
        return html` <slot name="fallback"></slot> `;
    }
}

defineElement('pages-watch-list-component', PagesWatchListComponent);
